<template>
  <div class="gtp-frame d-flex flex-column">
    <!-- Header -->
    <div class="gtp-header">
      <div class="gtp-back">
        <v-icon size="14" color="white">mdi-chevron-left</v-icon>
      </div>
      <span class="gtp-title">Genie Tax Planner</span>
    </div>

    <!-- Tax Planning accordion -->
    <div class="gtp-accordion">
      <div class="gtp-accordion-bar d-flex align-center justify-space-between" @click="expanded = !expanded">
        <div class="d-flex align-center" style="gap: 6px">
          <v-icon size="14" color="white">mdi-file-document-outline</v-icon>
          <span class="gtp-accordion-title">Tax Planning</span>
        </div>
        <v-icon size="14" color="white">{{ expanded ? 'mdi-chevron-up' : 'mdi-chevron-down' }}</v-icon>
      </div>

      <v-expand-transition>
        <div v-show="expanded" class="gtp-accordion-body">
          <!-- Suggest bar headline (hidden when admin leaves it blank) -->
          <div v-if="suggestBarText" class="gtp-suggest-bar">{{ suggestBarText }}</div>

          <!-- Feature icons -->
          <div v-if="enabledFeatures.length" class="gtp-feature-row">
            <div v-for="(feature, index) in enabledFeatures" :key="`gtp-feat-${index}`" class="gtp-feature">
              <v-img v-if="feature.imageUrl" :src="feature.imageUrl" width="44" height="44" class="gtp-feature-img" cover>
                <template #error>
                  <div class="gtp-feature-box d-flex align-center justify-center">
                    <v-icon color="grey-lighten-3" size="16">mdi-image-broken-variant</v-icon>
                  </div>
                </template>
              </v-img>
              <div v-else class="gtp-feature-box"></div>
              <span class="gtp-feature-label">
                {{ feature.messages?.thai || feature.messages?.english || `Feature${index + 1}` }}
              </span>
            </div>
          </div>

          <!-- Suggestion questions (2x2) -->
          <div v-if="enabledQuestions.length" class="gtp-question-grid">
            <div v-for="(question, index) in enabledQuestions" :key="`gtp-q-${index}`" class="gtp-question-chip">
              {{ question.messages?.thai || question.messages?.english || `Question ${index + 1}` }}
            </div>
          </div>
        </div>
      </v-expand-transition>
    </div>

    <!-- Chat conversation -->
    <div class="gtp-chat">
      <div class="gtp-msg d-flex align-end">
        <img :src="avatarUrl" class="gtp-avatar" alt="genie" />
        <div class="gtp-bubble">
          <p class="gtp-bubble-text">สวัสดีค่ะ Genie ช่วยวางแผนภาษีปี 2569 ให้คุณได้แบบง่าย ๆ ค่ะ คุณต้องการวางแผนภาษีปีนี้ใช่ไหมคะ</p>
          <div class="gtp-bubble-divider"></div>
          <div class="gtp-bubble-actions d-flex align-center">
            <img src="/lhb/heart.svg" width="14" class="mr-2" alt="like" />
            <img v-if="config.ttsEnabled" src="/lhb/volume.svg" width="14" class="mr-2" alt="speak" />
            <img v-if="config.copyMessage?.enabled" src="/lhb/copy.svg" width="10" alt="copy" />
          </div>
        </div>
      </div>

      <!-- Static reply suggestions -->
      <div class="gtp-replies">
        <div class="gtp-reply-chip">ใช่</div>
        <div class="gtp-reply-chip">ไม่ใช่ ต้องการปี 2568</div>
      </div>
    </div>

    <!-- Footer: languages + input + disclaimer -->
    <div class="gtp-footer">
      <div class="gtp-lang">
        <span>รองรับภาษา:</span>
        <template v-for="(lang, index) in enabledLanguages" :key="lang.key">
          <span class="gtp-lang-item">{{ lang.text }}</span>
          <span v-if="index < enabledLanguages.length - 1">,</span>
        </template>
      </div>
      <v-img :src="config.voice?.enabled ? '/lhb/chat.png' : '/lhb/chat-no-mic.png'" contain></v-img>
      <p v-if="config.postMessage?.enabled" class="gtp-disclaimer" :style="postMessageColor ? { color: postMessageColor } : null">{{ postMessageText }}</p>
    </div>
  </div>
</template>

<script>
export default {
  name: 'MobileTaxPlannerPreview',
  props: {
    features: { type: Array, default: () => [] },
    questions: { type: Array, default: () => [] },
    config: { type: Object, required: true },
    enabledLanguages: { type: Array, default: () => [] },
    postMessageText: { type: String, default: '' },
    postMessageColor: { type: String, default: null },
    suggestBarText: { type: String, default: '' },
    genieIconUrl: { type: String, default: null },
  },
  data() {
    return {
      expanded: true,
    };
  },
  computed: {
    enabledFeatures() {
      return (this.features || []).filter((f) => f && f.featureEnabled).slice(0, 4);
    },
    enabledQuestions() {
      return (this.questions || []).filter((q) => q && q.questionEnabled).slice(0, 4);
    },
    avatarUrl() {
      return this.genieIconUrl || '/lhb/listen.svg';
    },
  },
};
</script>

<style scoped>
.gtp-frame {
  width: 268px;
  height: 582px;
  border-radius: 40px;
  overflow: hidden;
  position: relative;
  background-image: url('/lhb/backgroundmock.png');
  background-position: center;
  background-size: cover;
  background-repeat: no-repeat;
  padding: 16px 12px 12px;
  color: #fff;
}

/* Header */
.gtp-header {
  position: relative;
  height: 30px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 12px;
}
.gtp-back {
  position: absolute;
  left: 0;
  width: 26px;
  height: 26px;
  border-radius: 50%;
  background: rgba(0, 0, 0, 0.28);
  display: flex;
  align-items: center;
  justify-content: center;
}
.gtp-title {
  font-size: 14px;
  font-weight: 700;
}

/* Accordion */
.gtp-accordion {
  background: rgba(18, 45, 40, 0.42);
  border: 1px solid rgba(255, 255, 255, 0.22);
  border-radius: 14px;
  padding: 10px 12px;
  margin-bottom: 14px;
}
.gtp-accordion-bar {
  cursor: pointer;
}
.gtp-accordion-title {
  font-size: 12px;
  font-weight: 700;
}
.gtp-accordion-body {
  padding-top: 10px;
}

/* Suggest bar */
.gtp-suggest-bar {
  font-size: 10px;
  font-weight: 500;
  line-height: 1.3;
  color: #d6dedd;
  margin-bottom: 8px;
}

/* Feature icons */
.gtp-feature-row {
  display: flex;
  justify-content: space-between;
  gap: 8px;
  margin-bottom: 12px;
}
.gtp-feature {
  width: 48px;
  display: flex;
  flex-direction: column;
  align-items: center;
}
.gtp-feature-img {
  border-radius: 8px;
}
.gtp-feature-box {
  width: 44px;
  height: 44px;
  background-color: rgba(245, 245, 245, 0.85);
  border: 1px solid #ccc;
  border-radius: 8px;
}
.gtp-feature-label {
  color: #fff;
  font-size: 8px;
  font-weight: 700;
  text-align: center;
  line-height: 1.1;
  margin-top: 4px;
}

/* Question chips */
.gtp-question-grid {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}
.gtp-question-chip {
  width: calc(50% - 4px);
  min-height: 30px;
  display: flex;
  align-items: center;
  justify-content: center;
  text-align: center;
  background: rgba(122, 122, 122, 0.15);
  backdrop-filter: blur(2px);
  border: 1px solid rgba(255, 255, 255, 0.6);
  border-radius: 16px;
  font-size: 8px;
  font-weight: 700;
  color: #fff;
  padding: 4px 8px;
}

/* Chat */
.gtp-chat {
  flex: 1;
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
  gap: 8px;
  min-height: 0;
}
.gtp-msg {
  gap: 6px;
}
.gtp-avatar {
  width: 28px;
  height: 28px;
  border-radius: 6px;
  flex-shrink: 0;
  object-fit: cover;
}
.gtp-bubble {
  background: rgba(18, 40, 38, 0.55);
  border-radius: 12px;
  padding: 8px 10px;
  max-width: 80%;
}
.gtp-bubble-text {
  margin: 0;
  font-size: 10px;
  line-height: 1.35;
}
.gtp-bubble-divider {
  height: 1px;
  background: rgba(255, 255, 255, 0.4);
  margin: 6px 0;
}
.gtp-bubble-actions img {
  opacity: 0.9;
}
.gtp-replies {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 6px;
}
.gtp-reply-chip {
  background: rgba(18, 40, 38, 0.35);
  border: 1px solid rgba(255, 255, 255, 0.5);
  border-radius: 14px;
  padding: 5px 16px;
  font-size: 9px;
}

/* Footer */
.gtp-footer {
  flex-grow: 0;
}
.gtp-lang {
  display: flex;
  align-items: center;
  gap: 4px;
  flex-wrap: wrap;
  font-size: 10px;
  color: #d6dedd;
  padding: 0 6px;
  margin-bottom: 4px;
}
.gtp-disclaimer {
  margin: 6px 0 0;
  font-size: 7px;
  text-align: center;
  color: #d6dedd;
}
</style>
